import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { API_BASE, getAuthHeaders, apiJson } from "../../../helpers/apiConfig";
import { downloadObjectsCsv } from "../../../helpers/csvExport";
import { iadeFallback, isMissingEndpoint } from "../../../helpers/financeFallback";

const IadeTalepleri = () => {
  document.title = "İade Talepleri | HarmanaGEL";

  const [iadeler, setIadeler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [selectedIade, setSelectedIade] = useState(null);
  const [newDurum, setNewDurum] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [bakiyeYukle, setBakiyeYukle] = useState(true);
  // AdminFinans uçları deploy edilmemişse OData yedek yolu kullanılır
  const [fallbackMode, setFallbackMode] = useState(false);
  const [warnMsg, setWarnMsg] = useState("");

  const fetchIadeler = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE}/IADE_TALEPLERI?$top=2000&$orderby=TALEP_TARIHI desc` +
          `&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress),SIPARIS_DETAY($select=ID,SIPARIS_ID)`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error(`Yükleme hatası (${response.status})`);
      const result = await response.json();
      if (result && result.value) setIadeler(result.value);
    } catch (err) {
      setError(err.message || "İade talepleri yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIadeler();
  }, []);

  const handleEdit = (iade) => {
    setSelectedIade(iade);
    setNewDurum(iade.DURUM || "Beklemede");
    setAciklama("");
    setBakiyeYukle(true);
    setModal(true);
  };

  const isTerminal = (d) => ["Onaylandı", "Reddedildi"].includes(d);

  const handleSave = async () => {
    if (!selectedIade) return;
    setActionLoading(true);
    setError(null);
    setWarnMsg("");
    try {
      // Onay → cüzdana yükleme, red → sadece kapatma, diğerleri → ara durum.
      // Üç uç da aynı sözleşmeyi kullanır: { TalepId, Durum, Aciklama, BakiyeYukle }
      const path =
        newDurum === "Onaylandı"
          ? "/api/AdminFinans/IadeOnayla"
          : newDurum === "Reddedildi"
            ? "/api/AdminFinans/IadeReddet"
            : "/api/AdminFinans/IadeDurumGuncelle";

      let data;
      try {
        data = await apiJson(path, {
          method: "POST",
          body: JSON.stringify({
            TalepId: selectedIade.ID,
            Durum: newDurum,
            Aciklama: aciklama || null,
            BakiyeYukle: newDurum === "Onaylandı" ? bakiyeYukle : undefined,
          }),
        });
        setFallbackMode(false);
      } catch (apiErr) {
        if (!isMissingEndpoint(apiErr)) throw apiErr;
        setFallbackMode(true);
        data = await iadeFallback(selectedIade, newDurum, aciklama, bakiyeYukle);
      }

      setSuccessMsg(data?.message || "İade talebi güncellendi.");
      if (data?.walletError) setWarnMsg(data.walletError);
      setModal(false);
      await fetchIadeler();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (err) {
      setError(err.message || "Güncelleme sırasında hata oluştu.");
    } finally {
      setActionLoading(false);
    }
  };

  const getDurumBadge = (durum) => {
    const renkler = {
      Beklemede: "warning",
      "İnceleniyor": "info",
      Onaylandı: "success",
      Reddedildi: "danger",
      "Kargo Bekleniyor": "primary",
    };
    return <Badge color={renkler[durum] || "secondary"}>{durum || "Bilinmiyor"}</Badge>;
  };

  const bekleyenCount = useMemo(
    () =>
      iadeler.filter((t) =>
        ["Beklemede", "İnceleniyor", "Kargo Bekleniyor"].includes(t.DURUM)
      ).length,
    [iadeler]
  );

  const exportCsv = () => {
    downloadObjectsCsv(
      `iade-talepleri-${new Date().toISOString().slice(0, 10)}.csv`,
      iadeler,
      [
        { key: "ID", label: "ID" },
        { label: "Müşteri", get: (t) => t.KULLANICILAR?.AD_SOYAD || t.KULLANICI_ID },
        { key: "IADE_NEDENI", label: "Neden" },
        { key: "IADE_TUTARI", label: "Tutar" },
        { key: "DURUM", label: "Durum" },
        { key: "KARGO_KODU", label: "Kargo" },
        {
          label: "Talep Tarihi",
          get: (t) =>
            t.TALEP_TARIHI ? new Date(t.TALEP_TARIHI).toLocaleString("tr-TR") : "",
        },
      ]
    );
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "ID",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
      },
      {
        header: "Müşteri",
        accessorKey: "KULLANICILAR",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell) => {
          const u = cell.getValue();
          return u ? (
            <div>
              <div className="fw-medium">{u.AD_SOYAD}</div>
              <small className="text-muted">{u.Mail_Adress}</small>
            </div>
          ) : (
            `ID: ${cell.row.original.KULLANICI_ID}`
          );
        },
      },
      {
        header: "İade Nedeni",
        accessorKey: "IADE_NEDENI",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell) => (
          <span title={cell.row.original.ACIKLAMA || ""}>{cell.getValue()}</span>
        ),
      },
      {
        header: "İade Tutarı",
        accessorKey: "IADE_TUTARI",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell) => (
          <span className="fw-bold text-danger">
            {parseFloat(cell.getValue() || 0).toLocaleString("tr-TR", {
              minimumFractionDigits: 2,
            })}{" "}
            ₺
          </span>
        ),
      },
      {
        header: "Kargo Kodu",
        accessorKey: "KARGO_KODU",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell) =>
          cell.getValue() ? (
            <span className="font-monospace small">{cell.getValue()}</span>
          ) : (
            <span className="text-muted">-</span>
          ),
      },
      {
        header: "Durum",
        accessorKey: "DURUM",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell) => getDurumBadge(cell.getValue()),
      },
      {
        header: "Talep Tarihi",
        accessorKey: "TALEP_TARIHI",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell) => {
          const d = cell.getValue();
          return d ? new Date(d).toLocaleString("tr-TR") : "-";
        },
      },
      {
        header: "Sonuçlanma",
        accessorKey: "SONUCLANMA_TARIHI",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell) => {
          const d = cell.getValue();
          return d ? new Date(d).toLocaleString("tr-TR") : "-";
        },
      },
      {
        header: "İşlemler",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cellProps) => {
          const row = cellProps.row.original;
          return (
            <div className="d-flex gap-1">
              <Button
                color="primary"
                size="sm"
                outline
                onClick={() => handleEdit(row)}
              >
                <i className="bx bx-edit" />
              </Button>
              {!isTerminal(row.DURUM) && (
                <>
                  <Button
                    color="success"
                    size="sm"
                    onClick={() => {
                      setSelectedIade(row);
                      setNewDurum("Onaylandı");
                      setAciklama("");
                      setModal(true);
                    }}
                  >
                    Onay
                  </Button>
                  <Button
                    color="danger"
                    size="sm"
                    outline
                    onClick={() => {
                      setSelectedIade(row);
                      setNewDurum("Reddedildi");
                      setAciklama("");
                      setModal(true);
                    }}
                  >
                    Red
                  </Button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="İade Talepleri" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && (
            <Alert color="danger" toggle={() => setError(null)}>
              {error}
            </Alert>
          )}

          {warnMsg && (
            <Alert color="warning" toggle={() => setWarnMsg("")}>
              <i className="bx bx-error me-1" />
              {warnMsg}
            </Alert>
          )}
          {fallbackMode && (
            <Alert color="info" className="small py-2">
              <i className="bx bx-info-circle me-1" />
              <strong>AdminFinans servisi yayında değil.</strong> İşlemler OData yedek
              yolundan yapılıyor: talep güncellemesi ve cüzdan yüklemesi ayrı adımlar
              hâlinde çalışır (tek transaction değil). Sonucu cüzdan sayfasından
              doğrulayın.
            </Alert>
          )}

          {bekleyenCount > 0 && (
            <Alert color="warning">
              <strong>{bekleyenCount} açık iade talebi</strong> var. Onaylandığında
              tutar müşteri cüzdanına yüklenir.
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h4 className="card-title mb-0">İade Talepleri</h4>
                    <div className="d-flex gap-2">
                      <Button
                        color="secondary"
                        size="sm"
                        outline
                        onClick={exportCsv}
                        disabled={!iadeler.length}
                      >
                        <i className="bx bx-export me-1" />
                        CSV
                      </Button>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={fetchIadeler}
                        disabled={isLoading}
                      >
                        <i className="bx bx-refresh me-1" />
                        Yenile
                      </Button>
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={iadeler}
                      isGlobalFilter={true}
                      SearchPlaceholder="İade talebi ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => !actionLoading && setModal(false)} centered>
        <ModalHeader toggle={() => !actionLoading && setModal(false)}>
          İade Talebi #{selectedIade?.ID}
        </ModalHeader>
        <ModalBody>
          {selectedIade && (
            <>
              <div className="mb-3">
                <label className="form-label">İade Nedeni</label>
                <p className="form-control-plaintext bg-light p-2 rounded">
                  {selectedIade.IADE_NEDENI}
                </p>
              </div>
              {selectedIade.ACIKLAMA && (
                <div className="mb-3">
                  <label className="form-label">Açıklama</label>
                  <p className="text-muted">{selectedIade.ACIKLAMA}</p>
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">İade Tutarı</label>
                <p className="fw-bold text-danger fs-5">
                  {parseFloat(selectedIade.IADE_TUTARI || 0).toLocaleString("tr-TR", {
                    minimumFractionDigits: 2,
                  })}{" "}
                  ₺
                </p>
              </div>

              {isTerminal(selectedIade.DURUM) ? (
                <Alert color="secondary">
                  Bu talep sonuçlanmış: <strong>{selectedIade.DURUM}</strong>
                </Alert>
              ) : (
                <>
                  {newDurum === "Onaylandı" && (
                    <Alert color="success" className="small">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="bakiyeYukleCheck"
                          checked={bakiyeYukle}
                          onChange={(e) => setBakiyeYukle(e.target.checked)}
                          disabled={actionLoading}
                        />
                        <label className="form-check-label" htmlFor="bakiyeYukleCheck">
                          <strong>
                            {parseFloat(selectedIade.IADE_TUTARI || 0).toLocaleString(
                              "tr-TR",
                              { minimumFractionDigits: 2 }
                            )}{" "}
                            ₺
                          </strong>{" "}
                          müşteri cüzdanına yüklensin
                        </label>
                      </div>
                      <div className="text-muted mt-1">
                        İade bedeli banka/kart üzerinden geri ödendiyse işareti kaldırın.
                      </div>
                    </Alert>
                  )}
                  {newDurum === "Reddedildi" && (
                    <Alert color="warning" className="small">
                      Reddettiğinizde para hareketi olmaz; kullanıcı bilgilendirilir.
                    </Alert>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Durumu Güncelle</label>
                    <Input
                      type="select"
                      value={newDurum}
                      onChange={(e) => setNewDurum(e.target.value)}
                      disabled={actionLoading}
                    >
                      <option value="Beklemede">Beklemede</option>
                      <option value="İnceleniyor">İnceleniyor</option>
                      <option value="Kargo Bekleniyor">Kargo Bekleniyor</option>
                      <option value="Onaylandı">Onaylandı (cüzdana iade)</option>
                      <option value="Reddedildi">Reddedildi</option>
                    </Input>
                  </div>
                  <div className="mb-0">
                    <label className="form-label">Not (opsiyonel)</label>
                    <Input
                      type="textarea"
                      rows={2}
                      value={aciklama}
                      onChange={(e) => setAciklama(e.target.value)}
                      disabled={actionLoading}
                      placeholder="Kullanıcıya gidecek not..."
                    />
                  </div>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => setModal(false)}
            disabled={actionLoading}
          >
            Kapat
          </Button>
          {selectedIade && !isTerminal(selectedIade.DURUM) && (
            <Button
              color={
                newDurum === "Onaylandı"
                  ? "success"
                  : newDurum === "Reddedildi"
                  ? "danger"
                  : "primary"
              }
              onClick={handleSave}
              disabled={actionLoading}
            >
              {actionLoading ? <Spinner size="sm" /> : "Kaydet"}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default IadeTalepleri;
